import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Send, Sparkles, Trash2, Download, FileText, Loader2, Save,
  User, Home, Edit3, Mail, RefreshCw, Building2,
} from "lucide-react";

const emptyState = () => ({
  letter_type: "property_specific",
  borrower: { name: "", entity: "", email: "", phone: "" },
  property_info: {
    address: "", city: "", state: "", zip_code: "",
    property_type: "", purchase_price: null,
  },
  max_purchase_price: null,
  max_ltv_pct: 70,
  estimated_loan_amount: null,
  notes: "",
});

const fmtMoney = (v) => (v == null || v === "" ? "—" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);

const errMsg = (e, fallback = "Something went wrong") => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d.length && typeof d[0]?.msg === "string") return d.map((x) => x.msg).join("; ");
  return e?.message || fallback;
};

async function openAuthedPdf(url, filename = "document.pdf") {
  try {
    const r = await api.get(url, { responseType: "blob" });
    const blobUrl = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.target = "_blank";
    a.rel = "noopener";
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
  } catch (e) {
    toast.error(errMsg(e, "Couldn't open PDF"));
  }
}

export default function AdminPreApprovalStudio() {
  const { user } = useAuth();
  const [state, setState] = useState(emptyState());
  const [sessionId, setSessionId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [messages, setMessages] = useState([{
    role: "ada",
    text: "Hi — I'm Ada. Let's build a Pre-Approval Letter.\n\nAre we writing a letter for a SPECIFIC property (buyer has a target), or a BUY-SIDE SHOPPING letter (buyer is house-hunting)?",
  }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [library, setLibrary] = useState([]);
  const chatScrollRef = useRef(null);

  const loadLibrary = () => api.get("/admin/marketing/preapprovals").then((r) => setLibrary(r.data || []));
  useEffect(() => { loadLibrary(); }, []);

  useEffect(() => {
    const el = chatScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  // Client-side loan-amount math mirror
  useEffect(() => {
    const price = state.letter_type === "property_specific"
      ? state.property_info?.purchase_price
      : state.max_purchase_price;
    if (price && state.max_ltv_pct) {
      const amt = Math.round((Number(price) * Number(state.max_ltv_pct) / 100) / 100) * 100;
      if (amt !== state.estimated_loan_amount) {
        setState((s) => ({ ...s, estimated_loan_amount: amt }));
      }
    }
  }, [state.letter_type, state.property_info?.purchase_price, state.max_purchase_price, state.max_ltv_pct]);

  // ---- Chat ----
  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "broker", text }]);
    setSending(true);
    try {
      const r = await api.post("/admin/marketing/preapproval/chat", {
        session_id: sessionId, message: text, state,
      });
      setSessionId(r.data.session_id);
      setState(r.data.state || state);
      setMessages((m) => [...m, {
        role: "ada", text: r.data.reply,
        ready_to_generate: r.data.ready_to_generate,
      }]);
    } catch (e) {
      toast.error(errMsg(e, "Ada couldn't reply"));
    } finally {
      setSending(false);
    }
  };

  // ---- Live preview ----
  const refreshPreview = async () => {
    try {
      const r = await api.post("/admin/marketing/preapproval/preview", { state },
        { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      setPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
    } catch (e) { /* silent */ }
  };
  useEffect(() => {
    // Preview when we have at minimum a borrower name + LTV
    if (state.borrower?.name && state.max_ltv_pct) {
      const t = setTimeout(refreshPreview, 400);
      return () => clearTimeout(t);
    }
  }, [state]);

  // ---- Generate & save ----
  const generateAndSave = async () => {
    setGenerating(true);
    try {
      const url = editingId
        ? `/admin/marketing/preapprovals/${editingId}`
        : `/admin/marketing/preapproval/generate`;
      const method = editingId ? "patch" : "post";
      const r = await api[method](url, { state });
      const lid = editingId || r.data.id;
      toast.success(editingId ? "Letter updated" : "Letter saved");
      setEditingId(lid);
      loadLibrary();
      await openAuthedPdf(`/admin/marketing/preapprovals/${lid}/pdf`, r.data.filename || "preapproval.pdf");
    } catch (e) {
      toast.error(errMsg(e, "Save failed"));
    } finally {
      setGenerating(false);
    }
  };

  const editLetter = async (lid) => {
    try {
      const r = await api.get(`/admin/marketing/preapprovals/${lid}`);
      const q = r.data;
      setEditingId(q.id);
      setSessionId(null);
      setState({
        letter_type: q.letter_type || "property_specific",
        borrower: { name: "", entity: "", email: "", phone: "", ...(q.borrower || {}) },
        property_info: {
          address: "", city: "", state: "", zip_code: "", property_type: "", purchase_price: null,
          ...(q.property_info || {}),
        },
        max_purchase_price: q.max_purchase_price ?? null,
        max_ltv_pct: q.max_ltv_pct ?? 70,
        estimated_loan_amount: q.estimated_loan_amount ?? null,
        notes: q.notes || "",
      });
      setMessages([{
        role: "ada",
        text: `Loaded letter for ${q.borrower?.name || "borrower"}. Edit anything in the panel, or tell me what to change.`,
      }]);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(errMsg(e, "Load failed"));
    }
  };

  const emailLetter = async (lid, borrower) => {
    const to = borrower?.email || window.prompt("Email this letter to (borrower email):");
    if (!to) return;
    if (!window.confirm(`Email this pre-approval letter to ${to}?`)) return;
    try {
      await api.post(`/admin/marketing/preapprovals/${lid}/email`, { to });
      toast.success(`Emailed to ${to}`);
      loadLibrary();
    } catch (e) {
      toast.error(errMsg(e, "Email send failed"));
    }
  };

  const startNewLetter = () => {
    const hasWork = !!(state.borrower.name || state.property_info.address
      || state.max_purchase_price || messages.length > 1);
    if (hasWork && !window.confirm("Discard current letter and start a new one?")) return;
    setState(emptyState());
    setSessionId(null);
    setEditingId(null);
    setPreviewUrl((p) => { if (p) URL.revokeObjectURL(p); return null; });
    setMessages([{
      role: "ada",
      text: "Fresh start. Property-specific or buy-side shopping?",
    }]);
  };

  // ---- Manual field edits ----
  const setBorrower = (k, v) => setState((s) => ({ ...s, borrower: { ...s.borrower, [k]: v } }));
  const setProp = (k, v) => setState((s) => ({ ...s, property_info: { ...s.property_info, [k]: v } }));
  const setTop = (k, v) => setState((s) => ({ ...s, [k]: v }));

  const canGenerate = !!(state.borrower?.name && state.max_ltv_pct &&
    (state.letter_type === "property_specific"
      ? (state.property_info?.address && state.property_info?.purchase_price)
      : state.max_purchase_price));

  return (
    <div className="min-h-screen bg-[#FBF8F1] p-6" data-testid="preapproval-studio">
      <div className="max-w-[1440px] mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558]">// Marketing</div>
            <h1 className="font-serif text-3xl font-bold">Pre-Approval Letter Studio</h1>
            <p className="text-sm text-[#6B6558] mt-1">
              Byrd-branded pre-approval letters for well-qualified buyers. Property-specific or buy-side shopping.
            </p>
            {editingId && (
              <div className="mt-2 inline-flex items-center gap-2 byrd-chip byrd-chip-gold" data-testid="pa-editing-badge">
                <Edit3 size={12} /> Editing saved letter — changes overwrite on Save
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={startNewLetter} className="byrd-btn byrd-btn-dark" data-testid="pa-new">
              <RefreshCw size={14} /> New Letter
            </button>
          </div>
        </div>

        {/* Letter type toggle */}
        <div className="byrd-card p-3 flex gap-2 items-center" data-testid="pa-type-toggle">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558] mr-2">// Letter type</div>
          <TypeButton
            active={state.letter_type === "property_specific"}
            onClick={() => setTop("letter_type", "property_specific")}
            icon={<Home size={13} />}
            label="Property-Specific"
            testId="pa-type-property"
          />
          <TypeButton
            active={state.letter_type === "buy_side_shopping"}
            onClick={() => setTop("letter_type", "buy_side_shopping")}
            icon={<Building2 size={13} />}
            label="Buy-Side Shopping"
            testId="pa-type-shopping"
          />
        </div>

        {/* Main split — chat + preview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Chat pane */}
          <div className="byrd-card flex flex-col h-[720px]">
            <div className="p-4 border-b border-[#E4DFD1] flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#C89434] text-white grid place-items-center">
                <Sparkles size={14} />
              </div>
              <div>
                <div className="font-serif font-bold">Ada</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#6B6558]">// Pre-approval assistant</div>
              </div>
              <button
                onClick={startNewLetter}
                className="ml-auto text-[11px] font-mono uppercase tracking-widest text-[#6B6558] hover:text-[#C89434] flex items-center gap-1"
                data-testid="pa-restart-chat"
                title="Clear the conversation and start a new letter"
              >
                <RefreshCw size={11} /> Restart
              </button>
            </div>
            <div ref={chatScrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "broker" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-3 py-2 rounded-md text-sm whitespace-pre-wrap ${
                    m.role === "broker" ? "bg-[#1A1A1A] text-white" : "bg-[#F3EEE0] text-[#1A1A1A]"
                  }`}>
                    {m.text}
                    {m.ready_to_generate && canGenerate && !editingId && (
                      <button
                        onClick={generateAndSave}
                        disabled={generating}
                        className="mt-2 block text-[#C89434] font-mono text-[10px] uppercase tracking-widest hover:text-[#8A6821]"
                        data-testid="pa-generate-inline"
                      >
                        {generating ? "Generating…" : "→ Generate PDF now"}
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-[#F3EEE0] px-3 py-2 rounded-md text-sm text-[#6B6558]">
                    <Loader2 size={12} className="animate-spin inline mr-2" /> Ada is thinking…
                  </div>
                </div>
              )}
            </div>
            <div className="p-3 border-t border-[#E4DFD1] flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                placeholder="Type your reply…"
                className="flex-1 px-3 py-2 border border-[#E4DFD1] rounded-md text-sm focus:outline-none focus:border-[#C89434]"
                data-testid="pa-input"
              />
              <button onClick={sendMessage} disabled={sending || !input.trim()}
                className="byrd-btn byrd-btn-dark" data-testid="pa-send">
                <Send size={14} />
              </button>
            </div>
          </div>

          {/* Fields + preview */}
          <div className="space-y-4">
            <PreApprovalFieldsPanel
              state={state}
              setBorrower={setBorrower}
              setProp={setProp}
              setTop={setTop}
            />

            {previewUrl ? (
              <div className="byrd-card p-2">
                <div className="flex items-center justify-between mb-2 px-2">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558]">// Live preview</div>
                  <div className="flex gap-2">
                    <button onClick={refreshPreview} className="text-[11px] text-[#C89434] font-mono">Refresh</button>
                    {editingId && (
                      <button onClick={() => emailLetter(editingId, state.borrower)}
                        className="byrd-btn byrd-btn-outline text-xs" data-testid="pa-email">
                        <Mail size={12} /> Email
                      </button>
                    )}
                    <button onClick={generateAndSave} disabled={!canGenerate || generating}
                      className="byrd-btn byrd-btn-dark text-xs" data-testid="pa-save">
                      {generating ? "Saving…" : (<><Save size={12} /> Save &amp; Download</>)}
                    </button>
                  </div>
                </div>
                <iframe src={previewUrl} title="Preview" className="w-full h-[520px] border border-[#E4DFD1] rounded" />
              </div>
            ) : (
              <div className="byrd-card p-10 text-center">
                <div className="w-14 h-14 mx-auto rounded-full bg-[#F3EEE0] grid place-items-center text-[#C89434]">
                  <FileText size={22} />
                </div>
                <h3 className="font-serif text-xl font-bold mt-4">Preview will appear here</h3>
                <p className="text-[#6B6558] mt-2 text-sm max-w-sm mx-auto">
                  Add the borrower's name and the LTV to see a live preview. Chat with Ada or fill fields
                  manually on the right.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Library */}
        <div className="byrd-card p-6">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558]">// Marketing Library</div>
              <h2 className="font-serif text-2xl font-bold">Saved Pre-Approval Letters</h2>
            </div>
            <div className="text-xs text-[#6B6558]">{library.length} saved</div>
          </div>
          {library.length === 0 ? (
            <div className="text-sm text-[#6B6558]">No saved letters yet. Build one above and click <b>Save &amp; Download</b>.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="pa-library">
                <thead>
                  <tr className="text-left border-b border-[#E4DFD1] text-[10px] uppercase tracking-widest text-[#6B6558]">
                    <th className="p-2">Borrower</th>
                    <th className="p-2">Type</th>
                    <th className="p-2">Property / Cap</th>
                    <th className="p-2">Loan Amount</th>
                    <th className="p-2">Created By</th>
                    <th className="p-2">Created</th>
                    <th className="p-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {library.map((q) => (
                    <tr key={q.id} className="border-b border-[#F3EEE0]">
                      <td className="p-2">
                        <div className="font-semibold">{q.borrower?.name || "—"}</div>
                        <div className="text-[11px] text-[#6B6558]">{q.borrower?.email || ""}</div>
                      </td>
                      <td className="p-2">
                        <span className="byrd-chip text-[10px]">
                          {q.letter_type === "buy_side_shopping" ? "Shopping" : "Property"}
                        </span>
                      </td>
                      <td className="p-2 text-xs">
                        {q.letter_type === "property_specific" ? (
                          <>
                            <div>{q.property_info?.address || "—"}</div>
                            <div className="text-[11px] text-[#6B6558]">
                              {[q.property_info?.city, q.property_info?.state].filter(Boolean).join(", ")}
                            </div>
                          </>
                        ) : (
                          <div>Up to {fmtMoney(q.max_purchase_price)}</div>
                        )}
                      </td>
                      <td className="p-2">
                        <div>{fmtMoney(q.estimated_loan_amount)}</div>
                        <div className="text-[10px] text-[#6B6558]">@ {q.max_ltv_pct || "—"}%</div>
                      </td>
                      <td className="p-2" data-testid={`pa-createdby-${q.id}`}>
                        <div className="text-xs">{q.created_by_name || "Byrd & CO"}</div>
                        <span className={`byrd-chip text-[9px] mt-0.5 ${q.created_by_role === "client" ? "byrd-chip-gold" : ""}`}>
                          {q.created_by_role === "client" ? "CLIENT" : "ADMIN"}
                        </span>
                      </td>
                      <td className="p-2 text-[11px] text-[#6B6558]">{new Date(q.created_at).toLocaleString()}</td>
                      <td className="p-2 text-right whitespace-nowrap">
                        <button
                          onClick={() => emailLetter(q.id, q.borrower)}
                          title={q.borrower?.email ? `Email to ${q.borrower.email}` : "Prompt for email"}
                          className="byrd-btn byrd-btn-outline text-xs mr-2" data-testid={`pa-email-${q.id}`}>
                          <Mail size={12} /> Email
                        </button>
                        <button
                          onClick={() => editLetter(q.id)}
                          className="byrd-btn byrd-btn-outline text-xs mr-2" data-testid={`pa-edit-${q.id}`}>
                          <Edit3 size={12} /> Edit
                        </button>
                        <button
                          onClick={() => openAuthedPdf(`/admin/marketing/preapprovals/${q.id}/pdf`, q.filename || "preapproval.pdf")}
                          className="byrd-btn byrd-btn-outline text-xs" data-testid={`pa-dl-${q.id}`}>
                          <Download size={12} /> PDF
                        </button>
                        <button onClick={async () => {
                          if (!window.confirm("Delete this saved letter?")) return;
                          await api.delete(`/admin/marketing/preapprovals/${q.id}`);
                          loadLibrary();
                        }} className="byrd-btn byrd-btn-outline text-xs ml-2 text-[#8A1F1A] border-[#E38380]" data-testid={`pa-del-${q.id}`}>
                          <Trash2 size={12} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TypeButton({ active, onClick, icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-mono uppercase tracking-widest transition-colors ${
        active
          ? "bg-[#1A1A1A] text-[#C89434]"
          : "bg-white border border-[#E4DFD1] text-[#2A2A2A] hover:bg-[#F3EEE0]"
      }`}
    >
      {icon} {label}
    </button>
  );
}

function PreApprovalFieldsPanel({ state, setBorrower, setProp, setTop }) {
  const b = state.borrower || {};
  const p = state.property_info || {};
  const isProperty = state.letter_type === "property_specific";
  return (
    <div className="byrd-card p-4">
      <div className="flex items-center gap-2 mb-3">
        <User size={14} className="text-[#C89434]" />
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558]">// Borrower</div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <Inp label="Full Name" value={b.name} onChange={(v) => setBorrower("name", v)} testId="pa-borrower-name" />
        <Inp label="Entity (LLC / Trust — optional)" value={b.entity} onChange={(v) => setBorrower("entity", v)} />
        <Inp label="Email" value={b.email} onChange={(v) => setBorrower("email", v)} testId="pa-borrower-email" />
        <Inp label="Phone" value={b.phone} onChange={(v) => setBorrower("phone", v)} />
      </div>

      {isProperty ? (
        <>
          <div className="flex items-center gap-2 mt-4 mb-2">
            <Home size={14} className="text-[#C89434]" />
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558]">// Property</div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <Inp label="Street Address" value={p.address} onChange={(v) => setProp("address", v)} testId="pa-property-address" />
            <Inp label="Property Type (optional)" value={p.property_type} onChange={(v) => setProp("property_type", v)} />
            <Inp label="City" value={p.city} onChange={(v) => setProp("city", v)} />
            <div className="grid grid-cols-2 gap-2">
              <Inp label="State" value={p.state} onChange={(v) => setProp("state", v)} />
              <Inp label="ZIP" value={p.zip_code} onChange={(v) => setProp("zip_code", v)} />
            </div>
            <NumInp label="Purchase Price ($)" value={p.purchase_price} onChange={(v) => setProp("purchase_price", v)} testId="pa-purchase-price" />
            <NumInp label="Maximum LTV (%)" value={state.max_ltv_pct} step="1" onChange={(v) => setTop("max_ltv_pct", v)} testId="pa-ltv" />
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 mt-4 mb-2">
            <Building2 size={14} className="text-[#C89434]" />
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558]">// Shopping ceiling</div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <NumInp label="Max Purchase Price ($)" value={state.max_purchase_price}
              onChange={(v) => setTop("max_purchase_price", v)} testId="pa-max-price" />
            <NumInp label="Maximum LTV (%)" value={state.max_ltv_pct} step="1"
              onChange={(v) => setTop("max_ltv_pct", v)} testId="pa-ltv" />
          </div>
        </>
      )}

      <div className="mt-4 grid grid-cols-1 gap-2 text-sm">
        <div className="p-2 rounded bg-[#FBF8F1] border border-[#E4DFD1] flex items-center justify-between">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558]">Estimated Loan Amount</div>
          <div className="font-serif text-xl font-bold text-[#C89434]" data-testid="pa-estimated-loan">
            {fmtMoney(state.estimated_loan_amount)}
          </div>
        </div>
      </div>

      <div className="mt-3">
        <label className="block">
          <span className="text-[10px] font-mono uppercase tracking-widest text-[#6B6558]">Notes (optional — appended above disclaimer)</span>
          <textarea
            value={state.notes || ""}
            onChange={(e) => setTop("notes", e.target.value)}
            rows={2}
            className="mt-0.5 w-full px-2 py-1.5 border border-[#E4DFD1] rounded text-sm focus:outline-none focus:border-[#C89434]"
            data-testid="pa-notes"
          />
        </label>
      </div>
    </div>
  );
}

function Inp({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="text-[10px] font-mono uppercase tracking-widest text-[#6B6558]">{label}</span>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="mt-0.5 w-full px-2 py-1.5 border border-[#E4DFD1] rounded text-sm focus:outline-none focus:border-[#C89434]"
        data-testid={testId} />
    </label>
  );
}

function NumInp({ label, value, onChange, step, testId }) {
  return (
    <label className="block">
      <span className="text-[10px] font-mono uppercase tracking-widest text-[#6B6558]">{label}</span>
      <input type="number" step={step || "1"} value={value == null ? "" : value}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
        className="mt-0.5 w-full px-2 py-1.5 border border-[#E4DFD1] rounded text-sm focus:outline-none focus:border-[#C89434]"
        data-testid={testId} />
    </label>
  );
}
